import React, { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from "@mui/material";
import { IInvestmentDetailField, IInvestmentDetailTableData } from "store/types";

export interface UpdateLookThroughState {
  selectedCharacteristics: string[];
  characteristicMeasures: Record<string, string>; // characteristic name -> measure name
}

interface UpdateLookThroughModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, Record<string, number>>) => void; // { [charName]: { [charDataName]: percentage } }
  columns: IInvestmentDetailField[];
  tableData: IInvestmentDetailTableData | null;
}

export const UpdateLookThroughModal: React.FC<UpdateLookThroughModalProps> = ({
  open,
  onClose,
  onSubmit,
  columns,
  tableData
}) => {
  const characteristics = columns.filter((col) => col.detail_type === "characteristic");
  const measures = columns.filter((col) => col.detail_type === "measure");

  const [state, setState] = useState<UpdateLookThroughState>({
    selectedCharacteristics: [],
    characteristicMeasures: {}
  });

  const handleCharacteristicChange = (characteristicName: string, checked: boolean) => {
    setState((prev) => ({
      ...prev,
      selectedCharacteristics: checked
        ? [...prev.selectedCharacteristics, characteristicName]
        : prev.selectedCharacteristics.filter((name) => name !== characteristicName)
    }));
  };

  const handleMeasureChange = (characteristicName: string, measure: string) => {
    setState((prev) => ({
      ...prev,
      selectedCharacteristics: prev.selectedCharacteristics.includes(characteristicName)
        ? prev.selectedCharacteristics
        : [...prev.selectedCharacteristics, characteristicName],
      characteristicMeasures: { ...prev.characteristicMeasures, [characteristicName]: measure }
    }));
  };

  const handleSubmit = () => {
    if (!tableData) return;

    // Filter characteristicMeasures to only include selected characteristics
    const filteredMeasures = Object.fromEntries(
      Object.entries(state.characteristicMeasures).filter(([characteristicName]) =>
        state.selectedCharacteristics.includes(characteristicName)
      )
    );

    // Transform data: { [charName]: { [charDataName]: percentage } }
    const transformedData: Record<string, Record<string, number>> = {};

    Object.entries(filteredMeasures).forEach(([charName, measureName]) => {
      // Find the column keys for the characteristic and measure
      const charColumn = tableData.columns.find((col) => col.title === charName);
      const measureColumn = tableData.columns.find((col) => col.title === measureName);

      if (!charColumn || !measureColumn) return;

      // Group rows by characteristic value and sum measure values
      const groups: Record<string, number> = {};
      let totalSum = 0;

      tableData.rows.forEach((row) => {
        const charCell = row[charColumn.key];
        const measureCell = row[measureColumn.key];

        // Only process rows that have values in both columns
        if (charCell?.value && measureCell?.value) {
          const charDataName = charCell.value;
          const measureValue = parseFloat(measureCell.value) || 0;

          if (groups[charDataName]) {
            groups[charDataName] += measureValue;
          } else {
            groups[charDataName] = measureValue;
          }

          totalSum += measureValue;
        }
      });

      // Convert sums to percentages
      const percentages: Record<string, number> = {};
      Object.entries(groups).forEach(([charDataName, sum]) => {
        percentages[charDataName] = totalSum > 0 ? (sum / totalSum) * 100 : 0;
      });

      transformedData[charName] = percentages;
    });

    onSubmit(transformedData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            p: 4,
            borderRadius: "16px",
            m: 2,
            maxWidth: "480px"
          }
        }
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2, position: "relative" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main", pr: 4 }}>
            Update look through for a deal
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: "text.secondary"
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ borderColor: "divider", mb: 2 }} />
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
          Select the categories you want to update
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Stack>
          {characteristics.map((characteristic, index) => (
            <Box key={characteristic.id}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.selectedCharacteristics.includes(characteristic.name)}
                      onChange={(e) => handleCharacteristicChange(characteristic.name, e.target.checked)}
                      sx={{
                        color: "primary.main",
                        "&.Mui-checked": {
                          color: "primary.main"
                        }
                      }}
                    />
                  }
                  label={characteristic.name}
                  sx={{
                    color: "primary.main",
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      fontWeight: 500
                    }
                  }}
                />

                <FormControl component="fieldset">
                  <RadioGroup
                    value={state.characteristicMeasures[characteristic.name] || ""}
                    onChange={(e) => handleMeasureChange(characteristic.name, e.target.value)}
                    row
                    sx={{
                      "& .MuiFormControlLabel-root": {
                        mr: 2
                      }
                    }}
                  >
                    {measures.map((measure) => (
                      <FormControlLabel
                        key={measure.id}
                        value={measure.name}
                        control={<Radio sx={{ color: "primary.main" }} />}
                        label={`By ${measure.name}`}
                        sx={{ color: "primary.main" }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Stack>
              {index < characteristics.length - 1 && <Divider sx={{ borderColor: "divider" }} />}
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <Divider sx={{ borderColor: "divider" }} />

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            minWidth: "80px",
            textTransform: "none",
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            minWidth: "80px",
            textTransform: "none",
            fontWeight: 500
          }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};
